import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

function ForwardsPage({ token }) {
  const [forwards, setForwards] = useState([]);
  const [banks, setBanks] = useState([]);                          // ← NEW
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingForward, setEditingForward] = useState(null);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [pendingEditForward, setPendingEditForward] = useState(null);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancellingForward, setCancellingForward] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [cancelForm, setCancelForm] = useState({
    cancellationDate: '', cancellationSpot: '', mtmSettledInr: ''
  });
  const [form, setForm] = useState({
    dealNo: '', dealDate: '', bank: '', currency: 'USDINR',
    fcAmount: '', forwardRate: '', spotRateAtBooking: '',
    windowStartDate: '', windowEndDate: '', notes: ''
  });

  useEffect(() => {
    loadForwards();
    loadBanks();                                                    // ← NEW
  }, []);

  const loadForwards = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/forwards`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setForwards(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ← NEW — load active banks from Bank Master
  const loadBanks = async () => {
    try {
      const res = await axios.get(`${CONFIG.API}/api/bills/banks`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setBanks(res.data);
    } catch (err) {
      console.error('Could not load banks', err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCancelFormChange = (e) => {
    const updated = { ...cancelForm, [e.target.name]: e.target.value };
    if (cancellingForward && (e.target.name === 'cancellationSpot')) {
      const spot    = parseFloat(e.target.value || 0);
      const fwdRate = parseFloat(cancellingForward.forwardRate || 0);
      const fcAvail = parseFloat(cancellingForward.fcAvailable || 0);
      const mtm     = (fwdRate - spot) * fcAvail;
      updated.mtmSettledInr = mtm.toFixed(2);
    }
    setCancelForm(updated);
  };

  const resetForm = () => {
    setForm({
      dealNo: '', dealDate: '', bank: '', currency: 'USDINR',
      fcAmount: '', forwardRate: '', spotRateAtBooking: '',
      windowStartDate: '', windowEndDate: '', notes: ''
    });
    setEditingForward(null);
    setShowForm(false);
  };

  const handleEditClick = (forward) => {
    if (forward.status === 'CANCELLED') {
      alert('Cancelled forwards cannot be edited.');
      return;
    }
    setPendingEditForward(forward);
    setShowEditWarning(true);
  };

  const handleEditConfirm = () => {
    const f = pendingEditForward;
    setShowEditWarning(false);
    setPendingEditForward(null);
    setForm({
      dealNo:            f.dealNo || '',
      dealDate:          f.dealDate || '',
      bank:              f.bank || '',
      currency:          f.currency || 'USDINR',
      fcAmount:          f.fcAmount || '',
      forwardRate:       f.forwardRate || '',
      spotRateAtBooking: f.spotRateAtBooking || '',
      windowStartDate:   f.windowStartDate || '',
      windowEndDate:     f.windowEndDate || '',
      notes:             typeof f.notes === 'string' ? f.notes : ''
    });
    setEditingForward(f);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleCancelClick = (forward) => {
    setCancellingForward(forward);
    setCancelForm({
      cancellationDate: '', cancellationSpot: '', mtmSettledInr: ''
    });
    setShowCancelForm(true);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!form.dealNo)          { alert('Please enter deal number'); return; }
    if (!form.dealDate)        { alert('Please enter deal date'); return; }
    if (!form.bank)            { alert('Please select a bank'); return; }
    if (!form.fcAmount)        { alert('Please enter FC amount'); return; }
    if (!form.forwardRate)     { alert('Please enter forward rate'); return; }
    if (!form.spotRateAtBooking) { alert('Please enter spot rate at booking'); return; }
    if (!form.windowStartDate) { alert('Please enter window start date'); return; }
    if (!form.windowEndDate)   { alert('Please enter window end date'); return; }

    const payload = {
      dealNo:            form.dealNo,
      dealDate:          form.dealDate,
      bank:              form.bank,
      currency:          form.currency,
      fcAmount:          parseFloat(form.fcAmount),
      forwardRate:       parseFloat(form.forwardRate),
      spotRateAtBooking: parseFloat(form.spotRateAtBooking),
      windowStartDate:   form.windowStartDate,
      windowEndDate:     form.windowEndDate,
      notes:             form.notes || null
    };

    try {
      if (editingForward) {
        await axios.put(
          `${CONFIG.API}/api/bills/forwards/${editingForward.forwardId}`,
          payload,
          { headers: { authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${CONFIG.API}/api/bills/forwards`,
          payload,
          { headers: { authorization: `Bearer ${token}` } }
        );
      }
      resetForm();
      loadForwards();
    } catch (err) {
      alert('Error saving forward. Please check all fields.');
    }
  };

  const handleCancelSubmit = async () => {
    if (!cancelForm.cancellationDate) { alert('Please enter cancellation date'); return; }
    if (!cancelForm.cancellationSpot) { alert('Please enter spot rate on cancellation'); return; }

    try {
      await axios.put(
        `${CONFIG.API}/api/bills/forwards/${cancellingForward.forwardId}/cancel`,
        {
          cancellationDate: cancelForm.cancellationDate,
          cancellationSpot: parseFloat(cancelForm.cancellationSpot),
          mtmSettledInr:    cancelForm.mtmSettledInr ? parseFloat(cancelForm.mtmSettledInr) : null
        },
        { headers: { authorization: `Bearer ${token}` } }
      );
      setShowCancelForm(false);
      setCancellingForward(null);
      loadForwards();
    } catch (err) {
      alert('Error cancelling forward. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      'OPEN':             'badge-open',
      'PART_ALLOCATED':   'badge-partial',
      'FULLY_ALLOCATED':  'badge-done',
      'CANCELLED':        'badge-cancelled'
    };
    return map[status] || 'badge-open';
  };

  const premium = (parseFloat(form.forwardRate || 0) -
                   parseFloat(form.spotRateAtBooking || 0)).toFixed(4);

  const totalBookedFc    = forwards.filter(f => f.status !== 'CANCELLED')
                                   .reduce((s, f) => s + parseFloat(f.fcAmount || 0), 0);
  const totalAvailableFc = forwards.filter(f => f.status !== 'CANCELLED')
                                   .reduce((s, f) => s + parseFloat(f.fcAvailable || 0), 0);

  const filteredForwards = forwards.filter(f => {
    const matchStatus   = filterStatus   === '' || f.status   === filterStatus;
    const matchCurrency = filterCurrency === '' || f.currency === filterCurrency;
    return matchStatus && matchCurrency;
  });

  // ← NEW — build bank options; if editing and the stored bank isn't in Bank Master,
  //          add it as an "(old)" option so the edit form shows it correctly.
  const bankOptions = () => {
    const masterNames = banks.map(b => b.bankName);
    const options = banks.map(b => (
      <option key={b.bankId} value={b.bankName}>
        {b.bankName}{b.branch ? ` — ${b.branch}` : ''}
      </option>
    ));
    if (form.bank && !masterNames.includes(form.bank)) {
      options.unshift(
        <option key="__old__" value={form.bank}>
          {form.bank} (old)
        </option>
      );
    }
    return options;
  };

  return (
    <div className="content">

      {/* Edit Warning Popup */}
      {showEditWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '28px',
            maxWidth: '460px', width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700',
                          color: '#1E293B', marginBottom: '12px' }}>
              ⚠️ Edit Forward Contract
            </div>
            <div style={{ fontSize: '14px', color: '#475569',
                          lineHeight: '1.6', marginBottom: '20px' }}>
              You can edit all details of this forward contract only if
              <strong> no allocations have been made against it</strong>.
              <br /><br />
              If allocations exist, you must <strong>Cancel</strong> this
              forward and create a new one.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-secondary"
                onClick={() => { setShowEditWarning(false); setPendingEditForward(null); }}>
                Close
              </button>
              <button className="btn-primary" onClick={handleEditConfirm}>
                Yes, Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Forward Form */}
      {showCancelForm && cancellingForward && (
        <div className="form-card" style={{ maxWidth: '520px', marginBottom: '20px',
                                            border: '1px solid #FCA5A5' }}>
          <div className="form-step" style={{ marginBottom: '16px', color: '#DC2626' }}>
            ❌ Cancel Forward — {cancellingForward.dealNo}
          </div>
          <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '14px' }}>
            Forward rate: <strong>{Number(cancellingForward.forwardRate).toFixed(4)}</strong>
            &nbsp;|&nbsp;
            FC available: <strong>{Number(cancellingForward.fcAvailable).toLocaleString()}</strong>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Cancellation date</label>
              <input type="date" name="cancellationDate"
                value={cancelForm.cancellationDate}
                onChange={handleCancelFormChange} />
            </div>
            <div className="form-field">
              <label>Spot rate on cancellation</label>
              <input type="number" name="cancellationSpot"
                value={cancelForm.cancellationSpot}
                onChange={handleCancelFormChange}
                placeholder="e.g. 84.50" />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>MTM settled INR (auto calculated — editable)</label>
              <input type="number" name="mtmSettledInr"
                value={cancelForm.mtmSettledInr}
                onChange={handleCancelFormChange}
                placeholder="Auto calculated when you enter spot rate" />
            </div>
            {cancelForm.cancellationSpot && (
              <div style={{
                background: parseFloat(cancelForm.mtmSettledInr) >= 0 ? '#F0FDF4' : '#FEF2F2',
                border: `1px solid ${parseFloat(cancelForm.mtmSettledInr) >= 0 ? '#BBF7D0' : '#FCA5A5'}`,
                borderRadius: '6px', padding: '8px 12px',
                fontSize: '13px', marginTop: '8px'
              }}>
                MTM = (Forward rate {Number(cancellingForward.forwardRate).toFixed(4)} −
                Cancellation spot {cancelForm.cancellationSpot}) ×
                FC available {Number(cancellingForward.fcAvailable).toLocaleString()} =
                <strong> ₹ {Number(cancelForm.mtmSettledInr).toLocaleString()}</strong>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary"
              onClick={() => { setShowCancelForm(false); setCancellingForward(null); }}>
              Close
            </button>
            <button className="btn-danger" onClick={handleCancelSubmit}>
              Confirm Cancel Forward
            </button>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total contracts</div>
          <div className="metric-value val-blue">
            {forwards.filter(f => f.status !== 'CANCELLED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Open</div>
          <div className="metric-value val-green">
            {forwards.filter(f => f.status === 'OPEN').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Booked FC</div>
          <div className="metric-value val-blue">
            {Number(totalBookedFc).toLocaleString()}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">FC available</div>
          <div className="metric-value val-green">
            {Number(totalAvailableFc).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="section-header">
        <div className="section-title">Forward contracts</div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New forward'}
        </button>
      </div>

      {/* New / Edit Form */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '720px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingForward ? '✏️ Edit forward' : '📋 New forward contract'}
          </div>

          {/* Row 1 — Deal no, Deal date, Bank */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Deal no</label>
              <input name="dealNo" value={form.dealNo}
                onChange={handleChange} placeholder="Bank deal reference" />
            </div>
            <div className="form-field">
              <label>Deal date</label>
              <input type="date" name="dealDate" value={form.dealDate}
                onChange={handleChange} />
            </div>

            {/* ← CHANGED: was a free-text input, now a Bank Master dropdown */}
            <div className="form-field">
              <label>Bank</label>
              <select name="bank" value={form.bank} onChange={handleChange}>
                <option value="">— Select bank —</option>
                {bankOptions()}
              </select>
            </div>
          </div>

          {/* Row 2 — Currency, FC amount, Forward rate */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange}>
                <option value="USDINR">USD/INR</option>
                <option value="EURINR">EUR/INR</option>
                <option value="GBPINR">GBP/INR</option>
                <option value="AEDINR">AED/INR</option>
                <option value="SGDINR">SGD/INR</option>
                <option value="AUDINR">AUD/INR</option>
              </select>
            </div>
            <div className="form-field">
              <label>Booked FC amount</label>
              <input type="number" name="fcAmount" value={form.fcAmount}
                onChange={handleChange} placeholder="0" />
            </div>
            <div className="form-field">
              <label>Forward rate</label>
              <input type="number" name="forwardRate" value={form.forwardRate}
                onChange={handleChange} placeholder="e.g. 85.50" />
            </div>
          </div>

          {/* Row 3 — Spot at booking, Premium (auto), Window dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Spot rate at booking</label>
              <input type="number" name="spotRateAtBooking"
                value={form.spotRateAtBooking}
                onChange={handleChange} placeholder="e.g. 84.50" />
            </div>
            <div className="form-field">
              <label>Premium (auto)</label>
              <input
                value={
                  form.forwardRate && form.spotRateAtBooking
                    ? premium
                    : '—'
                }
                disabled
                style={{
                  background: parseFloat(premium) >= 0 ? '#F0FDF4' : '#FEF2F2',
                  color: parseFloat(premium) >= 0 ? '#166534' : '#DC2626',
                  fontWeight: '600'
                }}
              />
            </div>
            <div className="form-field">
              <label>Window start date</label>
              <input type="date" name="windowStartDate"
                value={form.windowStartDate} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Window end date</label>
              <input type="date" name="windowEndDate"
                value={form.windowEndDate} onChange={handleChange} />
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any additional notes..."
                rows={3}
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit}>
              {editingForward ? '✓ Update forward' : '✓ Save forward'}
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: '12px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap'
      }}>
        <select
          value={filterCurrency}
          onChange={e => setFilterCurrency(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}
        >
          <option value="">All currencies</option>
          <option value="USDINR">USD/INR</option>
          <option value="EURINR">EUR/INR</option>
          <option value="GBPINR">GBP/INR</option>
          <option value="AEDINR">AED/INR</option>
          <option value="SGDINR">SGD/INR</option>
          <option value="AUDINR">AUD/INR</option>
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}
        >
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="PART_ALLOCATED">Part allocated</option>
          <option value="FULLY_ALLOCATED">Fully allocated</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        {(filterStatus !== '' || filterCurrency !== '') && (
          <button
            onClick={() => { setFilterStatus(''); setFilterCurrency(''); }}
            style={{
              padding: '7px 12px', border: '1px solid #CBD5E1',
              borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              background: '#F1F5F9', color: '#64748B'
            }}>
            ✕ Clear filters
          </button>
        )}
        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          Showing {filteredForwards.length} of {forwards.length} contracts
        </span>
      </div>

      {/* Forwards table */}
      <div className="table-wrap">
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : filteredForwards.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No forward contracts found. Try changing the filters.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Deal no</th>
                <th>Deal date</th>
                <th>Bank</th>
                <th>Currency</th>
                <th>Booked FC</th>
                <th>Forward rate</th>
                <th>Spot at booking</th>
                <th>Premium</th>
                <th>Window start</th>
                <th>Window end</th>
                <th>FC available</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredForwards.map(f => {
                const prem = (parseFloat(f.forwardRate || 0) -
                              parseFloat(f.spotRateAtBooking || 0)).toFixed(4);
                return (
                  <tr key={f.forwardId}>
                    <td>{f.dealNo}</td>
                    <td>{f.dealDate}</td>
                    <td>{f.bank}</td>
                    <td>{f.currency}</td>
                    <td>{Number(f.fcAmount).toLocaleString()}</td>
                    <td>{Number(f.forwardRate).toFixed(4)}</td>
                    <td>{Number(f.spotRateAtBooking).toFixed(4)}</td>
                    <td style={{
                      color: parseFloat(prem) >= 0 ? '#166534' : '#DC2626',
                      fontWeight: '600'
                    }}>
                      {prem}
                    </td>
                    <td>{f.windowStartDate}</td>
                    <td>{f.windowEndDate}</td>
                    <td>{Number(f.fcAvailable).toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(f.status)}`}>
                        {f.status}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {f.status !== 'CANCELLED' && (
                        <button
                          className="btn-secondary"
                          onClick={() => handleEditClick(f)}
                          style={{ marginRight: '6px' }}>
                          Edit
                        </button>
                      )}
                      {(f.status === 'OPEN' || f.status === 'PART_ALLOCATED') && (
                        <button
                          className="btn-danger"
                          onClick={() => handleCancelClick(f)}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

export default ForwardsPage;